import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import CloseIcon from 'assets/images/icon_close.svg';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import styles from './CopyFromMeetPopup.module.css';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { getFilteredMeets } from 'api/clubApi';
import { getSeasons, getActiveSeason } from 'api/season';
import { format } from 'date-fns';
import { MEET_TYPE, clubForId, meetTypeName, meetTypesFor } from 'utils/clubFeatures';

const meetStatusOptions = [
  { value: 'Complete', label: 'Completed' },
  { value: 'Upcoming', label: 'Upcoming' },
  { value: 'Draft', label: 'Draft' },
];

// The competitions a club can copy from, as the club reads them: Single Club and Multi Club, or just Competition
// for a club that cannot hold a multi-club meet (a school). Training sessions are not offered here and never were.
// The values are text because this filter holds its choice as text.
const meetTypeOptionsFor = (club) =>
  meetTypesFor(club)
    .filter((type) => type !== MEET_TYPE.TRAINING)
    .map((type) => ({ value: String(type), label: meetTypeName(club, type, 'filter') }));

const CopyFromMeetPopup = ({
  clubId,
  showPopup,
  closePopup,
  onSelectMeet,
}) => {
  const club = useSelector((state) => clubForId(state.user, clubId));
  const meetTypeOptions = meetTypeOptionsFor(club);
  const [isLoading, setIsLoading] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [meets, setMeets] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [activeSeasonId, setActiveSeasonId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Complete');
  const [selectedMeetType, setSelectedMeetType] = useState('1');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedMeet, setSelectedMeet] = useState(null);

  // Fetch seasons on mount
  const fetchSeasons = useCallback(async () => {
    if (!clubId) return;
    try {
      const [seasonsResponse, activeSeasonResponse] = await Promise.all([
        getSeasons(clubId),
        getActiveSeason(clubId),
      ]);

      const seasonsList = seasonsResponse.data || [];
      setSeasons(seasonsList);

      // Set current/active season as default
      if (activeSeasonResponse.data?.seasonID) {
        setActiveSeasonId(activeSeasonResponse.data.seasonID);
        setSelectedSeasonId(activeSeasonResponse.data.seasonID);
      } else if (seasonsList.length > 0) {
        setSelectedSeasonId(seasonsList[0].seasonID);
      }
    } catch (err) {
      console.error('Error fetching seasons:', err);
    }
  }, [clubId]);

  // Fetch meets based on filters
  const fetchMeets = useCallback(async () => {
    if (!clubId || !selectedSeasonId) return;

    setIsLoading(true);
    try {
      const response = await getFilteredMeets(
        clubId,
        [selectedStatus],
        selectedSeasonId,
        null
      );

      let filteredMeets = response.data || [];

      // Filter by meet type if not 'all'
      if (selectedMeetType !== 'all') {
        const meetTypeValue = parseInt(selectedMeetType, 10);
        filteredMeets = filteredMeets.filter(meet => meet.meetType === meetTypeValue);
      }

      // Sort by date descending (most recent first)
      filteredMeets.sort((a, b) => new Date(b.meetDate) - new Date(a.meetDate));

      setMeets(filteredMeets);
    } catch (err) {
      console.error('Error fetching meets:', err);
      setMeets([]);
    } finally {
      setIsLoading(false);
    }
  }, [clubId, selectedSeasonId, selectedStatus, selectedMeetType]);

  useEffect(() => {
    if (showPopup) {
      fetchSeasons();
    }
  }, [showPopup, fetchSeasons]);

  useEffect(() => {
    if (showPopup && selectedSeasonId) {
      fetchMeets();
    }
  }, [showPopup, selectedSeasonId, selectedStatus, selectedMeetType, fetchMeets]);

  const handleCopySettings = (meet) => {
    setSelectedMeet(meet);
    setShowConfirmModal(true);
  };

  const handleConfirmCopy = () => {
    if (selectedMeet) {
      // Attach the source season's name — users copy from previous seasons,
      // and the "Settings copied from" banner needs to say which one.
      const sourceSeason = seasons.find(
        (season) => season.seasonID === selectedSeasonId
      );
      onSelectMeet({ ...selectedMeet, seasonName: sourceSeason?.name || null });
      setShowConfirmModal(false);
      closePopup();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  // Limit the season picker to the current (active) season and the one
  // immediately before it — copying settings from older seasons isn't useful.
  const seasonOptions = useMemo(() => {
    if (!seasons.length) return [];
    const sorted = [...seasons].sort(
      (a, b) => new Date(b.startDate) - new Date(a.startDate)
    );
    const activeIdx = sorted.findIndex((s) => s.seasonID === activeSeasonId);
    // Anchor on the active season so a future/draft season can't push the
    // current one out of view; fall back to the two most recent if unknown.
    const limited =
      activeIdx === -1
        ? sorted.slice(0, 2)
        : sorted.slice(activeIdx, activeIdx + 2);
    return limited.map((season) => ({
      value: season.seasonID,
      label: season.name,
    }));
  }, [seasons, activeSeasonId]);

  if (!showPopup) {
    return null;
  }

  return (
    <>
      <div className="modalOverlay">
        <div className="modal bigModal">
          <div className="modalContent">
            <div className="modalTitle">Copy Settings from Existing Meet</div>

            <div className={styles.popupContent}>
              <div className={styles.filterSection}>
              <div className={styles.filterRow}>
                <div className={styles.filterItem}>
                  <CustomDropdown
                    label="Season"
                    name="season"
                    value={selectedSeasonId}
                    onChange={(name, value) => setSelectedSeasonId(value)}
                    options={seasonOptions}
                    placeholder="Select season"
                  />
                </div>
                <div className={styles.filterItem}>
                  <CustomDropdown
                    label="Status"
                    name="status"
                    value={selectedStatus}
                    onChange={(name, value) => setSelectedStatus(value)}
                    options={meetStatusOptions}
                    placeholder="Select status"
                  />
                </div>
                <div className={styles.filterItem}>
                  <CustomDropdown
                    label="Meet Type"
                    name="meetType"
                    value={selectedMeetType}
                    onChange={(name, value) => setSelectedMeetType(value)}
                    options={meetTypeOptions}
                    placeholder="Select type"
                  />
                </div>
              </div>
            </div>

            <div className={styles.meetsList}>
              {isLoading ? (
                <div className={styles.loadingMessage}>
                  <div className={styles.spinner}></div>
                  Loading meets...
                </div>
              ) : meets.length === 0 ? (
                <div className={styles.emptyMessage}>
                  No meets found matching the selected filters.
                </div>
              ) : (
                <div className={styles.meetsTable}>
                  <div className={styles.tableHeader}>
                    <div className={styles.headerCell}>Meet Name</div>
                    <div className={styles.headerCell}>Date</div>
                    <div className={styles.headerCell}>Location</div>
                    <div className={styles.headerCellDescription}>Description</div>
                  </div>
                  <div className={styles.tableBody}>
                    {meets.map((meet) => (
                      <div
                        key={meet.meetID}
                        className={styles.tableRow}
                        onClick={() => handleCopySettings(meet)}
                      >
                        <div className={styles.cell}>{meet.meetName}</div>
                        <div className={styles.cell}>{formatDate(meet.meetDate)}</div>
                        <div className={styles.cell}>{meet.location || '-'}</div>
                        <div className={styles.cellDescription}>
                          {meet.description || '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </div>

            <div className="goBack" onClick={closePopup}>
              <div className="goBackImage">
                <img src={BackIcon} alt="back" />
              </div>
              <div className="goBackText">Back</div>
            </div>
            <div className="closeBtn" onClick={closePopup}>
              <img src={CloseIcon} alt="close" />
            </div>
          </div>
        </div>
      </div>

      <CustomNewConfirm
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Copy Meet Settings?"
        description={`All settings for your new meet will be replaced with the settings from "${selectedMeet?.meetName}". This includes the event program, volunteer requirements, high jump settings, and points configuration.`}
        onConfirm={handleConfirmCopy}
        confirmButtonText="Copy Settings"
        confirmButtonVariant="primary"
        onCancel={() => setShowConfirmModal(false)}
        showWarning={true}
        warningText="This action will overwrite any settings you have already entered."
      />
    </>
  );
};

CopyFromMeetPopup.propTypes = {
  clubId: PropTypes.string.isRequired,
  showPopup: PropTypes.bool.isRequired,
  closePopup: PropTypes.func.isRequired,
  onSelectMeet: PropTypes.func.isRequired,
};

export default CopyFromMeetPopup;
